import {
  useEffect,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import type { Settings, Tone } from './types';
import {
  CURRENCIES,
  TONES,
  isCurrencyCode,
  normalizeCurrencyCode,
} from './types';

function Section({
  title,
  tint,
  children,
}: {
  title: string;
  tint?: string;
  children: ReactNode;
}) {
  return (
    <section className="eg-section" style={tint ? { background: tint } : undefined}>
      <h3 className="eg-section-title">{title}</h3>
      <div className="eg-section-body">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="eg-field">
      <span className="eg-field-label">{label}</span>
      {children}
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <div className="eg-grid">{children}</div>;
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [draftValue, setDraftValue] = useState(value);
  const isValidHex = /^#[0-9a-fA-F]{6}$/.test(draftValue);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const commitDraft = () => {
    if (!isValidHex) {
      setDraftValue(value);
      return;
    }

    onChange(draftValue.toUpperCase());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.currentTarget.blur();
  };

  return (
    <Field label={label}>
      <div className="eg-color-row">
        <input
          type="color"
          value={isValidHex ? draftValue : value}
          aria-label={`${label} color picker`}
          onChange={(e) => {
            const nextValue = e.target.value.toUpperCase();
            setDraftValue(nextValue);
            onChange(nextValue);
          }}
        />
        <input
          type="text"
          value={draftValue}
          aria-label={`${label} hex value`}
          onChange={(e) => setDraftValue(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={handleKeyDown}
          maxLength={7}
        />
      </div>
    </Field>
  );
}

const coerceNumber = (value: string) => {
  const nextValue = Number(value);

  return Number.isNaN(nextValue) ? 0 : nextValue;
};

const formatToneLabel = (tone: Tone) => tone.charAt(0).toUpperCase() + tone.slice(1);

const getCurrencyOptions = (selectedCurrencyCode: string) => {
  const normalizedCode = normalizeCurrencyCode(selectedCurrencyCode);

  if (!normalizedCode || isCurrencyCode(normalizedCode)) {
    return CURRENCIES;
  }

  return [
    { code: normalizedCode, symbol: normalizedCode, label: `${normalizedCode} (merchant default)` },
    ...CURRENCIES,
  ] as const;
};

function ButtonPreview({ settings }: { settings: Settings }) {
  const style = {
    background: settings.primaryColor,
    color: settings.buttonTextColor,
    borderRadius: `${settings.buttonBorderRadius}px`,
  };

  return (
    <div className="eg-preview">
      <button type="button" className="eg-preview-button" style={style}>
        {settings.replenishmentButtonText || 'Reorder now'}
      </button>
      <button type="button" className="eg-preview-button" style={style}>
        {settings.subscriptionButtonText || 'Subscribe'}
      </button>
      <button type="button" className="eg-preview-button" style={style}>
        {settings.crossSellButtonText || 'Add to cart'}
      </button>
    </div>
  );
}

function SettingsPanel({
  settings,
  onUpdate,
  onAnalyzeBrandVoice,
}: {
  settings: Settings;
  onUpdate: (settings: Settings) => void;
  onAnalyzeBrandVoice?: () => Promise<string>;
}) {
  const currencyOptions = getCurrencyOptions(settings.currencyCode);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!onAnalyzeBrandVoice) return;

    setAnalyzing(true);
    setAnalyzeError(null);

    try {
      const analysis = await onAnalyzeBrandVoice();
      onUpdate({ ...settings, existingEmailCopy: analysis });
    } catch (err) {
      setAnalyzeError(err instanceof Error ? err.message : 'Failed to analyze brand voice');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <>
      <Section title="Content" tint="var(--eg-tint-content)">
        <Field label="Brand name">
          <input
            type="text"
            aria-label="Brand name"
            value={settings.brandName}
            onChange={(e) => onUpdate({ ...settings, brandName: e.target.value })}
          />
        </Field>
        <Row>
          <Field label="Discount code (optional)">
            <input
              type="text"
              aria-label="Discount code"
              value={settings.discountCode}
              onChange={(e) => onUpdate({ ...settings, discountCode: e.target.value })}
            />
          </Field>
          <Field label="Currency">
            <select
              aria-label="Currency"
              value={settings.currencyCode}
              onChange={(e) => onUpdate({ ...settings, currencyCode: e.target.value })}
            >
              {currencyOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Button text — replenishment & winback">
            <input
              type="text"
              aria-label="Button text — replenishment and winback"
              value={settings.replenishmentButtonText}
              onChange={(e) => onUpdate({ ...settings, replenishmentButtonText: e.target.value })}
            />
          </Field>
          <Field label="Button text — subscription">
            <input
              type="text"
              aria-label="Button text — subscription"
              value={settings.subscriptionButtonText}
              onChange={(e) => onUpdate({ ...settings, subscriptionButtonText: e.target.value })}
            />
          </Field>
        </Row>
        <Row>
          <Field label="Button text — cross-sell">
            <input
              type="text"
              aria-label="Button text — cross-sell"
              value={settings.crossSellButtonText}
              onChange={(e) => onUpdate({ ...settings, crossSellButtonText: e.target.value })}
            />
          </Field>
          <Field label="Header — cross-sell">
            <input
              type="text"
              aria-label="Header — cross-sell"
              value={settings.crossSellHeader}
              onChange={(e) => onUpdate({ ...settings, crossSellHeader: e.target.value })}
            />
          </Field>
        </Row>
      </Section>

      <Section title="Style" tint="var(--eg-tint-style)">
        <Row>
          <ColorField
            label="Primary color"
            value={settings.primaryColor}
            onChange={(v) => onUpdate({ ...settings, primaryColor: v })}
          />
          <ColorField
            label="Button text color"
            value={settings.buttonTextColor}
            onChange={(v) => onUpdate({ ...settings, buttonTextColor: v })}
          />
        </Row>
        <Row>
          <ColorField
            label="Background color"
            value={settings.backgroundColor}
            onChange={(v) => onUpdate({ ...settings, backgroundColor: v })}
          />
          <ColorField
            label="Text color"
            value={settings.textColor}
            onChange={(v) => onUpdate({ ...settings, textColor: v })}
          />
        </Row>
        <Field label={`Button radius — ${settings.buttonBorderRadius}px`}>
          <div className="eg-slider-row">
            <input
              type="range"
              min={0}
              max={30}
              aria-label="Button radius"
              value={settings.buttonBorderRadius}
              onChange={(e) => onUpdate({
                ...settings,
                buttonBorderRadius: coerceNumber(e.target.value),
              })}
            />
            <div className="eg-slider-labels">
              <span>Square</span>
              <span>Round</span>
            </div>
          </div>
        </Field>
        <Row>
          <Field label="Product image max height — desktop (px)">
            <input
              type="number"
              min={0}
              aria-label="Product image max height desktop"
              value={settings.productImageMaxHeightDesktop}
              onChange={(e) => onUpdate({
                ...settings,
                productImageMaxHeightDesktop: coerceNumber(e.target.value),
              })}
            />
          </Field>
          <Field label="Product image max height — mobile (px)">
            <input
              type="number"
              min={0}
              aria-label="Product image max height mobile"
              value={settings.productImageMaxHeightMobile}
              onChange={(e) => onUpdate({
                ...settings,
                productImageMaxHeightMobile: coerceNumber(e.target.value),
              })}
            />
          </Field>
        </Row>
      </Section>

      <Section title="Copywriting" tint="var(--eg-tint-copywriting)">
        <div className="eg-field">
          <div className="eg-field-label-row">
            <span className="eg-field-label">
              Brand voice (paste sample copy or auto-detect from website)
            </span>
            {onAnalyzeBrandVoice && (
              <button
                type="button"
                className="eg-regenerate-field"
                onClick={handleAnalyze}
                disabled={analyzing}
              >
                {analyzing ? 'Analyzing…' : 'Detect from website'}
              </button>
            )}
          </div>
          <textarea
            rows={5}
            aria-label="Existing email copy"
            value={settings.existingEmailCopy}
            onChange={(e) => onUpdate({ ...settings, existingEmailCopy: e.target.value })}
          />
          {analyzeError && (
            <span className="eg-field-error">{analyzeError}</span>
          )}
        </div>
        <Field label="Tone">
          <select
            aria-label="Tone"
            value={settings.tone}
            onChange={(e) => onUpdate({ ...settings, tone: e.target.value as Tone })}
          >
            {TONES.map((tone) => (
              <option key={tone} value={tone}>
                {formatToneLabel(tone)}
              </option>
            ))}
          </select>
        </Field>
      </Section>

      <Section title="Button preview" tint="var(--eg-tint-preview)">
        <ButtonPreview settings={settings} />
      </Section>
    </>
  );
}

export default SettingsPanel;
